import { type BaseEntity, type BaseProjectMetadata, BaseProjectWorld, EntityCollection } from "@utilitydelta/clientside-event-sourcing";
import type { EncryptedProject } from "@utilitydelta/nosignup-js";
import { AccessLevel, ServerEvent } from '@utilitydelta/eventplanedb-client';
import { type SetColor_ProjectionResult, type SetColor_LocalEvent, type SetColor_EventInputs, SetColor_EventProcessor } from "./events/SetColor";
import { type ColorSquareCreated_ProjectionResult, type ColorSquareCreated_LocalEvent, type ColorSquareCreated_EventInputs, ColorSquareCreated_EventProcessor } from "./events/ColorSquareCreated";
import { type AddShareLink_ProjectionResult, type AddShareLink_LocalEvent, type AddShareLink_EventInputs, AddShareLink_EventProcessor } from "./events/AddShareLink";
import { type ProvideAccess_ProjectionResult, type ProvideAccess_LocalEvent, type ProvideAccess_EventInputs, ProvideAccess_EventProcessor } from "./events/ProvideAccess";
import { type DisableShareLink_ProjectionResult, type DisableShareLink_LocalEvent, type DisableShareLink_EventInputs, DisableShareLink_EventProcessor } from "./events/DisableShareLink";
import { type UserVisit_ProjectionResult, type UserVisit_LocalEvent, type UserVisit_EventInputs, UserVisit_EventProcessor } from "./events/UserVisit";
import { type AssignRandomUsername_ProjectionResult, type AssignRandomUsername_LocalEvent, type AssignRandomUsername_EventInputs, AssignRandomUsername_EventProcessor } from "./events/AssignRandomUsername";
import { AskedForSymmetricKey_EventInputs, AskedForSymmetricKey_EventProcessor, AskedForSymmetricKey_LocalEvent, AskedForSymmetricKey_ProjectionResult } from "./events/AskedForSymmetricKey";
import { ProvidedSymmetricKey_EventInputs, ProvidedSymmetricKey_EventProcessor, ProvidedSymmetricKey_LocalEvent, ProvidedSymmetricKey_ProjectionResult } from "./events/ProvidedSymmetricKey";

export interface ProjectMetadata extends BaseProjectMetadata {
    symmetricKey?: string;
    myColor: number; // Current user color
    gridSize: number; // Size of the grid
};

export interface ColoredSquare extends BaseEntity<ProjectEventType> {
    x: number; // X coordinate in the grid
    y: number; // Y coordinate in the grid
    color: number; // Color value of the square
}

export interface ShareLink extends BaseEntity<ProjectEventType> {
    isActive: boolean; // Whether the link is currently active
    isSingleUse: boolean; // Whether the link can only be used once
    accessLevel: AccessLevel; // Level of access granted by this link
    description?: string; // Optional description for the link
    expiresOn: number; // Timestamp when the link expires
    usageCount: number; // Number of users who have used this link to gain access
}

export interface AccessEntry extends BaseEntity<ProjectEventType> {
    userId?: string; // ID of the user who accessed the project
    clientId: string; // ID of the user who accessed the project
    shareLinkId: string; // ID of the share link used
    accessTime: number; // Timestamp when access was granted
    accessLevel: AccessLevel; // Level of access granted
}

export interface ProjectUser extends BaseEntity<ProjectEventType> {
    accessLevel: AccessLevel; // Access level of the user
    isOAuthUser: boolean; // Whether the user is logged in via OAuth
    accessUpdatedOn: number; // Timestamp when access was last updated
    lastSeenOn: number; // Timestamp of the last time the user was active
    color: number; // Color assigned to the user
    randomUsername?: string; // Randomly generated username for the user
}

export const ProjectEventType = {
    AddShareLink: 43,
    ProvideAccess: 45,
    DisableShareLink: 46,
    AskedForSymmetricKey: 47,
    ProvidedSymmetricKey: 48,
    SetColor: 100,
    ColorSquareCreated: 101,
    UserVisit: 103,
    AssignRandomUsername: 104,
} as const;

// Create a type from the values of the const object
export type ProjectEventType = typeof ProjectEventType[keyof typeof ProjectEventType];

export type ProjectWorldEntityTypes = 'coloredSquares' | 'shareLinks' | 'accessEntries' | 'projectUsers';

export type ProjectWorldEntities = ColoredSquare | ShareLink | AccessEntry | ProjectUser;

export type ProjectProjectionResult =
    | SetColor_ProjectionResult
    | ColorSquareCreated_ProjectionResult
    | AddShareLink_ProjectionResult
    | ProvideAccess_ProjectionResult
    | DisableShareLink_ProjectionResult
    | UserVisit_ProjectionResult
    | AssignRandomUsername_ProjectionResult
    | AskedForSymmetricKey_ProjectionResult
    | ProvidedSymmetricKey_ProjectionResult;

export type ProjectLocalEvent =
    | SetColor_LocalEvent
    | ColorSquareCreated_LocalEvent
    | AddShareLink_LocalEvent
    | ProvideAccess_LocalEvent
    | DisableShareLink_LocalEvent
    | UserVisit_LocalEvent
    | AssignRandomUsername_LocalEvent
    | AskedForSymmetricKey_LocalEvent
    | ProvidedSymmetricKey_LocalEvent;

export type ProjectEventInputs =
    | SetColor_EventInputs
    | ColorSquareCreated_EventInputs
    | AddShareLink_EventInputs
    | ProvideAccess_EventInputs
    | DisableShareLink_EventInputs
    | UserVisit_EventInputs
    | AssignRandomUsername_EventInputs
    | AskedForSymmetricKey_EventInputs
    | ProvidedSymmetricKey_EventInputs;


export function createServerEvent(
    event: ProjectLocalEvent,
    eventData: string
): ServerEvent<ProjectEventType> {
    return {
        et: event.et,
        cx: event.cx ?? 0,
        ex: event.ex,
        tp: event.tp,
        tm: event.tm,
        ev: btoa(eventData)
    };
}
export const LATEST_PROJECTION_VERSION = "11";

export default class ProjectWorld extends BaseProjectWorld<
    ProjectEventType,
    ProjectEventInputs,
    ProjectLocalEvent,
    ProjectWorldEntityTypes,
    ProjectWorldEntities,
    ProjectProjectionResult,
    ServerEvent<ProjectEventType>,
    ProjectMetadata
> {
    // Entity collections
    coloredSquares: EntityCollection<ColoredSquare> = new EntityCollection<ColoredSquare>();
    shareLinks: EntityCollection<ShareLink> = new EntityCollection<ShareLink>();
    accessEntries: EntityCollection<AccessEntry> = new EntityCollection<AccessEntry>();
    projectUsers: EntityCollection<ProjectUser> = new EntityCollection<ProjectUser>();
    // Add encryption support
    encryptedProject: EncryptedProject | undefined = undefined;

    getAllEntityCollections(): Record<string, EntityCollection<ProjectWorldEntities>> {
        return {
            coloredSquares: this.coloredSquares as EntityCollection<ProjectWorldEntities>,
            shareLinks: this.shareLinks as EntityCollection<ProjectWorldEntities>,
            accessEntries: this.accessEntries as EntityCollection<ProjectWorldEntities>,
            projectUsers: this.projectUsers as EntityCollection<ProjectWorldEntities>
        };
    }

    constructor(projectMetadata: ProjectMetadata) {
        // Initialize with default project metadata
        super(projectMetadata);

        // Register all event processors
        this.registerProcessors([
            new SetColor_EventProcessor(this),
            new ColorSquareCreated_EventProcessor(this),
            new AddShareLink_EventProcessor(this),
            new ProvideAccess_EventProcessor(this),
            new DisableShareLink_EventProcessor(this),
            new UserVisit_EventProcessor(this),
            new AssignRandomUsername_EventProcessor(this),
            new AskedForSymmetricKey_EventProcessor(this),
            new ProvidedSymmetricKey_EventProcessor(this),
        ]);
    }
}
